/**
 * 数字资产 · 角色/场景 资产卡片
 * - 封面:locked_image_url 或骨架
 * - 名字 + 关键维度 chips + 状态 Badge
 * - 删除按钮(hover 显示)
 */
import { ImageIcon, Trash2, Lock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import type { BrainAsset } from './types';

interface Props {
  asset: BrainAsset;
  onOpen?: (a: BrainAsset) => void;
  onDelete?: (a: BrainAsset) => void;
}

function topChips(asset: BrainAsset): string[] {
  const a = asset.attributes ?? {};
  if (asset.kind === 'character') {
    return [
      a.gender as string,
      a.age as string,
      a.role as string,
      a.filmStyle as string,
    ].filter(Boolean);
  }
  if (asset.kind === 'location') {
    return [
      a.kind as string,
      a.atmosphere as string,
      a.time as string,
      a.visualStyle as string,
    ].filter(Boolean);
  }
  if (asset.kind === 'prop') {
    return [
      a.category as string,
      a.material as string,
      a.style as string,
      a.variantName as string,
    ].filter(Boolean);
  }
  return [];
}

export function AssetCard({ asset, onOpen, onDelete }: Props) {
  const cover = asset.lockedImageUrl;
  const chips = topChips(asset);

  return (
    <div
      className={cn(
        'group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-lg border border-outline-variant bg-surface shadow-m1 transition hover:shadow-m2',
      )}
      onClick={() => onOpen?.(asset)}
      role="button"
      tabIndex={0}
    >
      <div className="relative aspect-[4/5] w-full bg-surface-variant">
        {cover ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={cover}
            alt={asset.name}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-outline">
            <ImageIcon className="h-10 w-10" />
          </div>
        )}
        <div className="absolute left-2 top-2">
          {asset.status === 'locked' ? (
            <Badge variant="success" className="gap-1">
              <Lock className="h-3 w-3" />
              已锁定
            </Badge>
          ) : (
            <Badge variant="warning">草稿</Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="absolute right-1 top-1 h-7 w-7 p-0 text-white opacity-0 backdrop-blur-sm transition group-hover:opacity-100 hover:bg-black/40"
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(asset);
          }}
          title="删除资产"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <div className="truncate text-sm font-semibold text-neutral-800">{asset.name}</div>
        <div className="flex min-h-[40px] flex-wrap content-start items-start gap-1">
          {chips.slice(0, 4).map((c) => (
            <span
              key={c}
              className="rounded bg-surface-variant px-1.5 py-0.5 text-[10px] text-neutral-600"
            >
              {c}
            </span>
          ))}
        </div>
        <div className="mt-auto text-[10px] text-outline">
          {asset.generations?.length ?? 0} 轮生成
        </div>
      </div>
    </div>
  );
}
